import { CircleCheck, CircleSlash, ArrowRight, Layers, Award, Lock, Eye } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { formatINR } from '@/lib/format';
import { inlineExpression } from '@/lib/calculation';

const STEP_ICON = {
  matched:            { icon: CircleCheck, tone: 'text-emerald-500',                 label: 'MATCHED' },
  excluded:           { icon: Lock,        tone: 'text-rose-500',                     label: 'EXCLUDED' },
  skipped:            { icon: CircleSlash, tone: 'text-muted-foreground/60',          label: 'SKIPPED' },
  shadowed:           { icon: Eye,         tone: 'text-amber-600',                    label: 'SHADOWED' },
  winner:             { icon: ArrowRight,  tone: 'text-primary',                      label: 'WINNER' },
  excluded_winner:    { icon: Lock,        tone: 'text-rose-600',                     label: 'EXCLUSION WINS' },
  cap_applied:        { icon: Layers,      tone: 'text-amber-500',                    label: 'CAP APPLIED' },
  cap_spillover:      { icon: ArrowRight,  tone: 'text-emerald-500',                  label: 'SPILLOVER' },
  milestone:          { icon: Award,       tone: 'text-accent',                       label: 'MILESTONE' },
  milestone_crossed:  { icon: Award,       tone: 'text-accent',                       label: 'MILESTONE CROSSED' },
};

const PORTION_META = {
  primary:   { tone: 'border-primary/30 bg-primary/5',                              label: 'Primary' },
  spillover: { tone: 'border-amber-500/30 bg-amber-500/5',                          label: 'Spillover' },
  milestone: { tone: 'border-accent/30 bg-accent/5',                                label: 'Milestone' },
};

export const TraceViewer = ({ trace, breakdown }) => {
  if (!trace || trace.length === 0) {
    return (
      <Card className="p-6 border-dashed text-center text-xs text-muted-foreground" data-testid="admin-trace-empty">
        Run a trace simulation to see how rules were evaluated step-by-step.
      </Card>
    );
  }

  const portions = breakdown?.portions || [];

  return (
    <div data-testid="admin-trace-viewer" className="space-y-3">
      {/* Reward math breakdown */}
      {breakdown && (
        <Card className="p-3 space-y-2.5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Reward math</div>

          {/* Headline stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Total reward (raw)" value={`${(breakdown.reward_raw_total ?? 0).toFixed(2)} ${breakdown.currency || ''}`} />
            <Stat label="Total ₹ value" value={formatINR(breakdown.reward_value_inr_total ?? 0)} accent />
            <Stat label="Effective rate" value={`${(breakdown.effective_rate_pct ?? 0).toFixed(2)}%`} />
            <Stat
              label={breakdown.cap_applied ? 'Cap binding on' : 'Cap'}
              value={breakdown.cap_applied
                ? (breakdown.cap_binding_dimension === 'reward_points' ? 'reward (pts)'
                   : breakdown.cap_binding_dimension === 'reward_inr' ? 'reward (₹)'
                   : breakdown.cap_binding_dimension === 'spend' ? 'spend (₹)'
                   : 'cap applied')
                : 'none'}
            />
          </div>

          {/* Portion sub-blocks (primary / spillover / milestone) */}
          {portions.length > 0 && (
            <div className="space-y-1.5" data-testid="admin-trace-portions">
              {portions.map((p, i) => {
                const meta = PORTION_META[p.kind] || PORTION_META.primary;
                return (
                  <div key={i} className={cn('rounded-md border px-2 py-1.5 text-[11px]', meta.tone)} data-testid={`admin-trace-portion-${p.kind}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-[9px] uppercase tracking-wider opacity-70">{meta.label}</span>
                        <span className="font-medium truncate">{p.rule_label}</span>
                        {p.kind === 'spillover' && p.fallback_route === 'explicit' && (
                          <span
                            className="text-[9px] uppercase tracking-wider px-1.5 py-px rounded border border-accent/30 bg-accent/10 text-accent shrink-0"
                            title="Routed via an explicit fallback_rule_id set by the admin (vs auto/priority routing)"
                            data-testid="admin-trace-spillover-explicit-badge"
                          >
                            explicit
                          </span>
                        )}
                      </div>
                      <div className="font-mono-num font-semibold">
                        +{(p.reward_raw || 0).toFixed(2)} {p.currency || breakdown.currency || ''} <span className="opacity-70">· {formatINR(p.reward_value_inr || 0)}</span>
                      </div>
                    </div>
                    {p.note && <div className="text-[10px] opacity-80 mt-0.5">{p.note}</div>}
                    {p.kind !== 'milestone' && p.eligible_amount_inr > 0 && (
                      <>
                        <div className="text-[10px] opacity-60 mt-0.5 font-mono-num">on ₹{Number(p.eligible_amount_inr).toLocaleString('en-IN')} eligible spend</div>
                        <div className="text-[10px] opacity-80 mt-0.5 font-mono-num" data-testid={`admin-trace-portion-${p.kind}-math`}>
                          {inlineExpression(p)}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Math string (verbatim equation) */}
          {breakdown.math_string && (
            <div className="text-[11px] font-mono-num bg-[hsl(var(--surface-2))] rounded px-2 py-1.5 border border-border whitespace-pre-wrap break-words" data-testid="admin-trace-math">
              {breakdown.math_string}
            </div>
          )}
        </Card>
      )}

      {/* Step-by-step trace timeline */}
      <Card className="p-2">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground px-1.5 mb-1">Evaluation timeline</div>
        <div className="relative">
          <div className="absolute left-3 top-2 bottom-2 w-px bg-border" />
          <ol className="space-y-1">
            {trace.map((s, i) => {
              const cfg = STEP_ICON[s.action] || { icon: ArrowRight, tone: 'text-muted-foreground', label: (s.action || '').toUpperCase() };
              const Icon = cfg.icon;
              const isWinner = s.action === 'winner' || s.action === 'excluded_winner';
              const isMilestone = s.action === 'milestone' || s.action === 'milestone_crossed';
              const isSpillover = s.action === 'cap_spillover';
              return (
                <li key={i} className="relative pl-8" data-testid={`admin-trace-step-${i}`}>
                  <span className={cn(
                    'absolute left-0 top-0.5 h-6 w-6 rounded-full border bg-[hsl(var(--background))] grid place-items-center',
                    isWinner ? 'border-primary/40' :
                    isMilestone ? 'border-accent/40' :
                    isSpillover ? 'border-emerald-500/40' :
                    'border-border',
                  )}>
                    <Icon className={cn('h-3 w-3', cfg.tone)} />
                  </span>
                  <div className={cn(
                    'rounded-md px-2 py-1.5 text-[11px] border',
                    isWinner ? 'border-primary/30 bg-primary/5' :
                    isMilestone ? 'border-accent/30 bg-accent/5' :
                    isSpillover ? 'border-emerald-500/30 bg-emerald-500/5' :
                    (s.action === 'excluded' || s.action === 'excluded_winner') ? 'border-rose-500/20 bg-rose-500/5' :
                    s.action === 'matched' ? 'border-emerald-500/20 bg-emerald-500/5' :
                    s.action === 'shadowed' ? 'border-amber-500/20 bg-amber-500/5' :
                    s.action === 'cap_applied' ? 'border-amber-500/20 bg-amber-500/5' :
                    'border-border',
                  )}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={cn('text-[9px] uppercase tracking-wide', cfg.tone)}>{cfg.label}</Badge>
                      <span className="font-medium">{s.rule_label}</span>
                      {s.priority !== undefined && s.priority !== 0 && !isMilestone && !isSpillover && (
                        <span className="text-muted-foreground">priority {s.priority}</span>
                      )}
                    </div>
                    <div className="text-muted-foreground mt-0.5">{s.reason}</div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </Card>
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className="rounded-md border border-border bg-[hsl(var(--surface-2))] px-2 py-1.5">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={cn('text-sm font-mono-num font-semibold', accent && 'text-primary')}>{value}</div>
  </div>
);
